<template>
  <div>
    <div class="tw:mb-2 tw:flex tw:items-center tw:gap-1 tw:font-bold">
      {{ getLocaleText("stream_filters_sentiment.label") }}

      <QIcon name="info">
        <StreemTooltip
          anchor="top middle"
          self="bottom middle"
        >
          {{ getLocaleText("stream_filters_sentiment.tooltip") }}
        </StreemTooltip>
      </QIcon>
    </div>
    <div class="tw:grid tw:grid-cols-2 tw:gap-2">
      <div
        v-for="field in sentimentFields"
        :key="field"
      >
        <Checkbox
          v-model="selectedSentiment"
          :color="field"
          :data-testid="`option-${field}`"
          dense
          :label="getLocaleText(`sentiment.${field}`)"
          new-design
          :value="field"
        />
      </div>
      <div>
        <Checkbox
          v-model="unratedSentiment"
          data-testid="option-none"
          dense
          :label="getLocaleText('sentiment.none')"
          new-design
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import StreemTooltip from "shared/components/base/StreemTooltip.vue";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import sentimentOptions, {
  type SentimentOption,
} from "shared/components/core/pickers/SentimentPicker/sentimentOptions";
import { type StreamFiltersMinMax } from "shared/helpers/StreamFilters";

const sentiment = defineModel<StreamFiltersMinMax[]>("sentiment", {
  default: () => [],
});

const sentimentEnabled = defineModel<boolean>("sentimentEnabled");
const unratedSentiment = defineModel<boolean>("unratedSentiment");

const sentimentFields: SentimentOption["field"][] = [
  "negative",
  "positive",
  "trending_negative",
  "trending_positive",
  "balanced",
];

const selectedSentiment = computed<SentimentOption["field"][]>({
  get() {
    return sentimentOptions.flatMap((sentimentOption) => {
      const matches = sentiment.value.some((range) => {
        const [min, max] = sentimentOption.range;

        return (
          range.min === min ||
          range.max === max ||
          (Number(range.min) < min && Number(range.max) > max)
        );
      });

      return matches ? [sentimentOption.field] : [];
    });
  },
  set(values: string[]) {
    const newSentiment: StreamFiltersMinMax[] = [];

    sentimentOptions.forEach((sentimentOption, index) => {
      if (!values.includes(sentimentOption.field)) {
        return;
      }

      const [min, max] = sentimentOption.range;
      const previousOption = sentimentOptions[index - 1];

      if (previousOption && values.includes(previousOption.field)) {
        const previousRange = newSentiment[newSentiment.length - 1];
        previousRange.max = Math.max(max, Number(previousRange.max));
        previousRange.min = Math.min(min, Number(previousRange.min));
      } else {
        newSentiment.push({ min, max });
      }
    });

    sentimentEnabled.value = Boolean(newSentiment.length);
    sentiment.value = newSentiment;
  },
});
</script>
